// ============================================================================
// IDENTITÉS & ACCÈS · APPLICATIONS — nos applications web, une carte chacune : comment elle se connecte par l'IdP
// Orgazone (Connexion) et l'adresse que la Console sonde pour la page Santé (Surveillance). Un objet, un endroit :
// la sonde vivait sous « Paramètres → Supervision », l'enregistrement IdP ici, sans que l'un renvoie à l'autre.
//
// ⚠️ §23.0 : tout est lu. La connexion vient de l'IdP (`/admin/clients`), la surveillance des sondes en base et de
//    leurs relevés ; les deux se rapprochent par leur adresse (`platform-apps.ts`). Une source muette se DIT sur
//    chaque carte, jamais remplacée par un « aucune ».
// ⚠️ LA CONNEXION SE LIT ICI, ELLE NE S'Y RÈGLE PAS ENCORE : les applications de l'IdP sont déclarées dans sa
//    configuration (`clients.ts`). Les modifier depuis la Console, c'est les enregistrer en base de l'IdP (SA-3.4) —
//    ce chantier touche toutes les connexions, il ne se glisse pas dans un écran. La SURVEILLANCE, elle, se règle
//    entièrement ici : créer, modifier, activer, retirer une sonde.
// ⚠️ LES APPLICATIONS DES APPAREILS (caisse, Mobile Partner…) suivent, dans leur propre bloc (`device-apps.tsx`) : leur
//    nom au store et ce que le store de cet environnement en dit.
// ============================================================================
import { useEffect, useId, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { alert, confirm, EmptyState, Icon, showToast, useOverlayPanel } from '@orgazone/ui-web';
import { api, type RpApp, type ServiceHealth } from '../api';
import { glassBoxSt, glassOverlaySt } from '../glass-modal';
import { platformApps, type PlatformApp } from '../platform-apps';
import { DeviceAppsSection } from './device-apps';
import { fmtUptime, HEALTH_STATUS } from '../service-health';
import { formPanelRef, Pill } from '../ui';

/** Une adresse sondée se valide comme le serveur la valide : http(s), sans espace. */
const isProbeUrl = (u: string) => /^https?:\/\/\S+$/.test(u);

const eyebrowSt: CSSProperties = { fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)' };
const factKeySt: CSSProperties = { fontSize: 'var(--t-sub)', color: 'var(--ink500)' };
const factValSt: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink-800)', marginTop: 2, overflowWrap: 'anywhere' };
const noteSt: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55 };

/** Un encadré d'information de la maquette (« i » dans un carré teinté). */
function InfoBox({ tone, children }: { tone: 'in' | 'wa'; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', border: '1px solid var(--ink200)', borderRadius: 'var(--r-md)', background: `var(--${tone}-bg)` }}>
      <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 5, background: `color-mix(in srgb, var(--${tone}-fg) 22%, transparent)`, color: `var(--${tone}-fg)`, display: 'grid', placeItems: 'center', fontSize: 'var(--t-meta)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>i</span>
      <div style={{ fontSize: 'var(--t-dense)', color: `var(--${tone}-fg)`, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

/** Un fait du bloc Connexion : son nom en petit, sa valeur en mono. */
function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div>
      <div style={factKeySt}>{k}</div>
      <div className="mono" style={factValSt}>{children}</div>
    </div>
  );
}

/** Ce que l'IdP accepte de l'application — ou pourquoi on ne peut pas le dire. */
function ConnexionFacts({ app, idpErr }: { app: PlatformApp; idpErr: string | null }) {
  if (app.provider) {
    return <div style={noteSt}>C'est le fournisseur de connexion lui-même : les autres applications s'enregistrent auprès de lui, lui ne s'enregistre nulle part.</div>;
  }
  if (app.rp) {
    return (
      <>
        <Fact k="identifiant">{app.rp.clientId}</Fact>
        <Fact k="adresses de retour">
          {app.rp.redirectUris.length ? app.rp.redirectUris.map((u) => <div key={u}>{u}</div>) : '—'}
        </Fact>
        <Fact k="flux">{app.rp.grantTypes.join(' · ') || '—'}</Fact>
      </>
    );
  }
  // ⚠️ Un IdP muet ne vaut pas « aucune application » : on ne SAIT pas, et la carte le dit.
  if (idpErr !== null) return <div style={{ ...noteSt, color: 'var(--wa-fg)' }}>L’IdP n’a pas répondu : {idpErr}</div>;
  return <div style={noteSt}>Aucune application enregistrée auprès de l’IdP à cette adresse.</div>;
}

/** La sonde de l'application : son adresse, son état, sa dernière mesure — ou son absence. */
function SurveillanceFacts({ app, svcErr, busy, onToggle, onAdd }: {
  app: PlatformApp; svcErr: string | null; busy: boolean; onToggle: () => void; onAdd: () => void;
}) {
  const s = app.service;
  if (!s) {
    if (svcErr !== null) return <div style={{ ...noteSt, color: 'var(--wa-fg)' }}>Les sondes n’ont pas pu être lues : {svcErr}</div>;
    return (
      <>
        <div style={noteSt}>Aucune sonde : la Console ne mesure pas cette application.</div>
        <button type="button" className="btn-ghost sm" style={{ alignSelf: 'flex-start' }} onClick={onAdd}>Ajouter une sonde</button>
      </>
    );
  }
  const ms = s.median ?? s.lastMs;
  const measured = s.enabled && ms !== null;
  return (
    <>
      <div>
        <div style={factKeySt}>adresse sondée</div>
        <div className="mono" style={{ ...factValSt, fontSize: 'var(--t-sub)', color: 'var(--ink600)' }}>{s.url}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Pill tone={s.enabled ? 'ok' : 'ne'} shape="tight">{s.enabled ? 'sonde active' : 'sonde inactive'}</Pill>
        {measured && (
          <span className="mono" data-latency style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: HEALTH_STATUS[s.status].fg }}>
            {ms} ms · {fmtUptime(s.uptime)}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {/* Le libellé dit l'ACTION du clic ; l'état vit dans la pastille à côté. */}
        <button type="button" className="btn-ghost sm" disabled={busy} onClick={onToggle}
          aria-label={`${s.enabled ? 'Désactiver' : 'Activer'} la sonde de ${app.name}`}>
          {s.enabled ? 'Désactiver' : 'Activer'}
        </button>
      </div>
      {measured && <Link to="/sante" className="btn-link marque" style={{ alignSelf: 'flex-start', fontSize: 'var(--t-note)' }}>Voir sa santé →</Link>}
    </>
  );
}

/** La carte d'une application : son identité, sa connexion, sa surveillance. */
function AppCard({ app, idpErr, svcErr, busy, onEdit, onToggle }: {
  app: PlatformApp; idpErr: string | null; svcErr: string | null; busy: boolean; onEdit: () => void; onToggle: () => void;
}) {
  return (
    <div className="dcard" data-app={app.key} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{app.name}</div>
          <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 2, overflowWrap: 'anywhere' }}>
            {app.origin ?? 'adresse illisible'}
          </div>
        </div>
        {app.provider && <Pill tone="vi" shape="tight">fournisseur de connexion</Pill>}
        <div style={{ flex: 1 }} />
        <button type="button" className="btn-ghost sm" onClick={onEdit} aria-label={`Modifier ${app.name}`}>Modifier</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', flex: 1 }}>
        <div data-block="connexion" style={{ padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0 }}>
          <div className="mono" style={eyebrowSt}>Connexion</div>
          <ConnexionFacts app={app} idpErr={idpErr} />
        </div>
        <div data-block="surveillance" style={{ padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0, background: 'var(--panel)' }}>
          <div className="mono" style={eyebrowSt}>Surveillance</div>
          <SurveillanceFacts app={app} svcErr={svcErr} busy={busy} onToggle={onToggle} onAdd={onEdit} />
        </div>
      </div>
    </div>
  );
}

/**
 * La modale d'une application : sa connexion (lue), puis sa surveillance (réglée). Sans `app`, une NOUVELLE application
 * — c'est-à-dire une sonde neuve, sa connexion se déclarant dans la configuration de l'IdP.
 */
export function AppEditor({ app, onClose, onSaved }: { app: PlatformApp | null; onClose: () => void; onSaved: (notice: string) => void }) {
  const s = app?.service ?? null;
  const [name, setName] = useState(s?.name ?? app?.rp?.name ?? app?.name ?? '');
  const [url, setUrl] = useState(s?.url ?? '');
  const [enabled, setEnabled] = useState(s?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const titleId = useId();
  const nameId = useId();
  const urlId = useId();
  const probeGroup = useId();
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });

  const draft = { name: name.trim(), url: url.trim() };
  const patch: { name?: string; url?: string; enabled?: boolean } = s ? {
    ...(draft.name !== s.name ? { name: draft.name } : null),
    ...(draft.url !== s.url ? { url: draft.url } : null),
    ...(enabled !== s.enabled ? { enabled } : null),
  } : {};
  // Ce qui empêche d'enregistrer, DIT sur le bouton (maquette) plutôt qu'un bouton grisé sans raison.
  const blocker = !draft.name ? 'Nom obligatoire'
    : !isProbeUrl(draft.url) ? 'Adresse sondée invalide'
      : s && Object.keys(patch).length === 0 ? 'Aucune modification'
        : null;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (blocker) return;
    setErr(null); setBusy(true);
    try {
      if (s) {
        await api.updateService(s.id, patch);
        onSaved(`Surveillance de « ${draft.name} » enregistrée.`);
      } else {
        const { service } = await api.createService(draft);
        // ⚠️ Une sonde naît active : la demander inactive est un second geste, qui peut échouer seul — le dire.
        if (!enabled) {
          try { await api.updateService(service.id, { enabled: false }); }
          catch (e2) { onSaved(`Sonde de « ${draft.name} » créée, mais ACTIVE : ${(e2 as Error).message}`); return; }
        }
        onSaved(`Sonde de « ${draft.name} » créée.`);
      }
    } catch (e2) { setErr((e2 as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!s) return;
    const ok = await confirm({
      title: `Retirer la sonde de « ${s.name} » ?`,
      message: 'La Console cessera de mesurer cette application, et la page Santé ne la montrera plus. Son enregistrement auprès de l’IdP n’est pas touché.',
      confirmLabel: 'Retirer',
    });
    if (!ok) return;
    setErr(null); setBusy(true);
    try { await api.deleteService(s.id); onSaved(`Sonde de « ${s.name} » retirée.`); }
    catch (e2) { setErr((e2 as Error).message); setBusy(false); }
  };

  const title = app ? `Modifier « ${app.name} »` : 'Nouvelle application';
  const inputSt: CSSProperties = { minHeight: 36, height: 36, background: 'var(--elev)' };
  const labelSt: CSSProperties = { display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 };
  const sectionSt: CSSProperties = { border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 };

  return (
    <div style={glassOverlaySt} onClick={busy ? undefined : onClose}>
      <form style={{ ...glassBoxSt(600), outline: 'none' }} ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={save}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'flex-start', gap: 14, flexShrink: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 id={titleId} style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>{title}</h3>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5 }}>
              {app?.rp
                ? <><span className="mono">{app.rp.clientId}</span> · <span className="mono">🔒 identifiant figé — le changer casse toutes les sessions</span></>
                : app?.provider
                  ? 'Fournisseur de connexion : seule la surveillance se règle ici.'
                  : app
                    ? 'Aucune application de l’IdP à cette adresse : seule la surveillance se règle ici.'
                    : 'La Console la mesure dès l’enregistrement. Sa connexion se déclare dans la configuration de l’IdP : elle rejoindra cette carte d’elle-même, par son adresse.'}
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} disabled={busy} title="Fermer" aria-label="Fermer">✕</button>
        </div>

        <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {app?.rp && (
            <div data-block="connexion" style={sectionSt}>
              <div className="mono" style={eyebrowSt}>Connexion</div>
              <Fact k="nom affiché par l’IdP">{app.rp.name}</Fact>
              <Fact k="adresses de retour">{app.rp.redirectUris.length ? app.rp.redirectUris.map((u) => <div key={u}>{u}</div>) : '—'}</Fact>
              <Fact k="flux">{app.rp.grantTypes.join(' · ') || '—'}</Fact>
              <div style={noteSt}>
                Déclarée dans la configuration de l’IdP : ses adresses de retour et ses flux s’y modifient. Les régler d’ici
                attend l’enregistrement des applications en base de l’IdP (SA-3.4).
              </div>
            </div>
          )}
          <div data-block="surveillance" style={sectionSt}>
            <div className="mono" style={eyebrowSt}>Surveillance</div>
            <div>
              <label htmlFor={nameId} style={labelSt}>Nom affiché</label>
              <input id={nameId} className="field-input" style={inputSt} value={name} onChange={(e) => setName(e.target.value)} placeholder="Back-office, EDI…" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, alignItems: 'end' }}>
              <div>
                <label htmlFor={urlId} style={labelSt}>Adresse sondée</label>
                <input id={urlId} className="field-input mono" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/v1/ready"
                  aria-invalid={draft.url !== '' && !isProbeUrl(draft.url)}
                  style={{ ...inputSt, fontSize: 'var(--t-body)', ...(draft.url !== '' && !isProbeUrl(draft.url) ? { borderColor: 'color-mix(in srgb, var(--da-fg) 50%, transparent)' } : null) }} />
              </div>
              <div>
                <span id={probeGroup} style={labelSt}>Sonde</span>
                <div role="group" aria-labelledby={probeGroup} style={{ display: 'flex', gap: 6 }}>
                  {([[true, 'active'], [false, 'inactive']] as const).map(([v, l]) => (
                    <button key={l} type="button" aria-pressed={enabled === v} className={enabled === v ? 'row-pick selected' : 'row-pick'}
                      onClick={() => setEnabled(v)} style={{ height: 36, padding: '0 13px', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'inherit' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={noteSt}>Visez <span className="mono">/v1/ready</span> : il vérifie les dépendances de l’application et rend le détail brique par brique.</div>
          </div>
          {err && <div className="err">{err}</div>}
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: 'var(--ink-50)', flexShrink: 0 }}>
          <div style={{ flex: '1 1 200px', minWidth: 0, fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.45 }}>
            Une adresse sondée erronée fait passer l’application au rouge sur la page Santé. La modification vaut dès le prochain relevé.
          </div>
          {s && <button type="button" className="btn-ghost sm danger" onClick={() => void remove()} disabled={busy}>Retirer la sonde</button>}
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className="btn-cta" disabled={busy || blocker !== null}
            style={blocker ? { background: 'var(--ink200)', color: 'var(--ink400)', boxShadow: 'none', cursor: 'not-allowed' } : undefined}>
            {busy ? 'Enregistrement…' : blocker ?? 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function Applications() {
  const [services, setServices] = useState<ServiceHealth[] | null>(null);
  const [svcErr, setSvcErr] = useState<string | null>(null);
  const [idp, setIdp] = useState<{ apps: RpApp[]; issuer: string | null } | null>(null);
  const [idpErr, setIdpErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlatformApp | 'new' | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = () => {
    api.healthServices().then((r) => { setServices(r.services); setSvcErr(null); }).catch((e) => setSvcErr((e as Error).message));
    api.identityApps().then((r) => { setIdp(r); setIdpErr(null); }).catch((e) => setIdpErr((e as Error).message));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (app: PlatformApp) => {
    const s = app.service;
    if (!s) return;
    setBusyKey(app.key);
    try {
      await api.updateService(s.id, { enabled: !s.enabled });
      showToast(`Sonde de « ${s.name} » ${s.enabled ? 'désactivée' : 'activée'}.`);
      load();
    } catch (e) { void alert({ title: 'Sonde inchangée', message: (e as Error).message }); }
    finally { setBusyKey(null); }
  };

  // ⚠️ Chaque source se lit à part : la page attend les DEUX réponses (ou leurs refus) avant de rendre des cartes, sans
  //    quoi une sonde s'afficherait d'abord « sans connexion » puis en gagnerait une.
  const settled = (services !== null || svcErr !== null) && (idp !== null || idpErr !== null);
  const apps = settled ? platformApps(services ?? [], idp?.apps ?? null, idp?.issuer ?? null) : [];

  return (
    <div className="page">
      {editing && (
        <AppEditor app={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
          onSaved={(n) => { setEditing(null); showToast(n); load(); }} />
      )}
      <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px 16px', marginBottom: 16 }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <h1 className="page-title">Applications</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
            Nos applications, une carte chacune : comment elle se connecte par l’IdP Orgazone, et l’adresse que la Console sonde pour la page Santé.
          </div>
        </div>
        <button className="btn-cta" onClick={() => setEditing('new')} style={{ gap: 7, flexShrink: 0 }}>
          <Icon name="plus" size={14} />Nouvelle application
        </button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <InfoBox tone="in">
          <b>Connexion</b> : ce que l’IdP accepte de l’application — où renvoyer l’utilisateur après sa connexion, et par
          quels flux. <b>Surveillance</b> : l’adresse que la Console interroge chaque minute pour la page Santé.
        </InfoBox>
      </div>

      {!settled ? <div className="muted">Chargement…</div>
        : apps.length === 0 ? (
          <div className="dcard">
            {svcErr !== null && <div className="err" style={{ margin: 16 }}>Les sondes n’ont pas pu être lues : {svcErr}</div>}
            {idpErr !== null && <div className="err" style={{ margin: 16 }}>L’IdP n’a pas répondu : {idpErr}</div>}
            {svcErr === null && idpErr === null && (
              <EmptyState icon="grid" title="Aucune application"
                note="Ajoutez une sonde avec « Nouvelle application » ; les applications déclarées à l’IdP apparaissent ici d’elles-mêmes." />
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(440px, 100%), 1fr))', gap: 14 }}>
            {apps.map((a) => (
              <AppCard key={a.key} app={a} idpErr={idpErr} svcErr={svcErr} busy={busyKey === a.key}
                onEdit={() => setEditing(a)} onToggle={() => void toggle(a)} />
            ))}
          </div>
        )}

      <div style={{ marginTop: 14 }}>
        <InfoBox tone="wa">
          Sondez <b>/v1/ready</b>, jamais <b>/v1/health</b> ni la racine. <b>/v1/health</b> ne prouve que la vie du processus :
          il reste vert pendant qu’une base est morte. À la racine, un chemin inconnu tombe dans le repli de l’application,
          répond 200 avec du HTML, et la page Santé mesure la lecture d’un fichier statique. L’adresse doit être joignable
          depuis le conteneur de la Console : c’est de là que part la sonde.
        </InfoBox>
      </div>

      <DeviceAppsSection />
    </div>
  );
}
